
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OnboardingFormValues } from "../OnboardingFormSchema";
import { useWizard } from "../FormWizardContext";
import { PlusCircle, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const COLLABORATION_PERIODS = [
  { label: "Less than a month", value: "less_than_month" },
  { label: "1 month", value: "1_month" },
  { label: "3 months", value: "3_months" },
  { label: "6 months", value: "6_months" },
  { label: "More than a year", value: "more_than_year" },
  { label: "Custom", value: "custom" },
];

export const RegionAndScopeStep = ({ form }: { form: UseFormReturn<OnboardingFormValues> }) => {
  const { currentStep } = useWizard();
  const [newLink, setNewLink] = useState("");
  const [showCustomDate, setShowCustomDate] = useState(false);

  if (currentStep !== 3) return null;

  const contentLinks = form.watch("content_links") || [];

  const handleAddLink = () => {
    if (!newLink) return;

    // Check if it's a valid URL
    try {
      new URL(newLink);
      const updatedLinks = [...contentLinks, newLink];
      form.setValue("content_links", updatedLinks);
      setNewLink("");
    } catch (e) {
      form.setError("content_links", { 
        type: "manual", 
        message: "Please enter a valid URL" 
      });
    }
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = contentLinks.filter((_, i) => i !== index);
    form.setValue("content_links", updatedLinks);
  };
  
  const handleCollaborationPeriodChange = (value: string) => {
    form.setValue("collaboration_period", value);
    setShowCustomDate(value === "custom");
    
    // If not custom, clear the start and end dates
    if (value !== "custom") {
      form.setValue("start_date", new Date());
      form.setValue("end_date", undefined);
    }
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="collaboration_period"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel className="text-gray-700 dark:text-gray-200">
              The collaboration time period for the compensation that you are applying for
            </FormLabel>
            <FormControl>
              <RadioGroup 
                onValueChange={(value) => handleCollaborationPeriodChange(value)}
                defaultValue={field.value}
                className="flex flex-wrap gap-2"
              >
                {COLLABORATION_PERIODS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={option.value} 
                      id={option.value}
                      className="peer sr-only"
                    />
                    <label
                      htmlFor={option.value}
                      className="flex items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-900 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50 peer-data-[state=checked]:text-blue-600 cursor-pointer"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {showCustomDate && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="start_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-gray-700 dark:text-gray-200">Start Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="end_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-gray-700 dark:text-gray-200">End Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      fromDate={form.watch("start_date")}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      <FormField
        control={form.control}
        name="deliverables"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">
              Deliverables agreed upon with the contact person
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder="Describe the deliverables you've agreed to provide..."
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="content_links"
        render={() => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">
              Please input your content link(s) for the compensation you are applying for if you have started the content
            </FormLabel>
            <div className="space-y-2">
              <div className="flex gap-2">
                <FormControl>
                  <Input
                    placeholder="https://example.com"
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex-grow"
                  />
                </FormControl>
                <Button 
                  type="button" 
                  onClick={handleAddLink}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <PlusCircle className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {contentLinks.map((link, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary"
                    className="flex items-center gap-1 py-1.5 px-3"
                  >
                    <span className="text-xs max-w-[200px] truncate">{link}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-transparent"
                      onClick={() => handleRemoveLink(index)}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove</span>
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="feedback"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-gray-700 dark:text-gray-200">
              What is your feedback about working with Mantle and communicating with the contact person?
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder="Share your feedback here..."
                className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

